(() => {
  if (customElements.get('dyefor-sticky-purchase')) return;
  const visible = element => {
    if (!element || element.hidden || !element.getClientRects().length) return false;
    const style = getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
  };
  const text = element => (element?.textContent || '').replace(/\s+/g, ' ').trim();

  class StickyPurchase extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-sticky-action]');
      this.label = this.querySelector('[data-sticky-label]');
      this.price = this.querySelector('[data-sticky-price]');
      this.variant = this.querySelector('[data-sticky-variant]');
      this.image = this.querySelector('img');
      this.onUpdate = () => this.schedule();
      this.onAction = () => this.activate();
      this.onProduct = event => { event.preventDefault(); this.returnToOptions(); };
      this.button.addEventListener('click', this.onAction);
      this.querySelector('[data-sticky-product-link]').addEventListener('click', this.onProduct);
      window.addEventListener('scroll', this.onUpdate, { passive: true });
      window.addEventListener('resize', this.onUpdate);
      window.addEventListener('pageshow', this.onUpdate);
      window.visualViewport?.addEventListener('resize', this.onUpdate);
      document.addEventListener('change', this.onUpdate);
      document.addEventListener('focusin', this.onUpdate);
      document.addEventListener('focusout', this.onUpdate);
      this.observer = new MutationObserver(changes => {
        if (changes.some(change => !this.contains(change.target))) this.schedule();
      });
      this.observer.observe(document.body, {
        subtree: true, childList: true, characterData: true, attributes: true,
        attributeFilter: ['class', 'style', 'hidden', 'open', 'aria-hidden', 'aria-disabled', 'disabled', 'src', 'srcset', 'value'],
      });
      this.schedule();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.chatObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      this.frame = null;
      clearTimeout(this.busyTimer);
      this.busy = false;
      this.chatRoot = null;
      window.removeEventListener('scroll', this.onUpdate);
      window.removeEventListener('resize', this.onUpdate);
      window.removeEventListener('pageshow', this.onUpdate);
      window.visualViewport?.removeEventListener('resize', this.onUpdate);
      document.removeEventListener('change', this.onUpdate);
      document.removeEventListener('focusin', this.onUpdate);
      document.removeEventListener('focusout', this.onUpdate);
      this.button.removeEventListener('click', this.onAction);
      this.querySelector('[data-sticky-product-link]').removeEventListener('click', this.onProduct);
      document.body.classList.remove('dyefor-sticky-purchase-visible');
    }

    schedule() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = null; if (this.isConnected) this.update(); });
    }

    controls() {
      const section = document.querySelector('[id^="MainProduct-"]');
      const info = section?.querySelector('[id^="ProductInfo-"]');
      const buy = info?.querySelector('product-form button[name="add"]');
      const customiser = info?.querySelector('dyefor-customizer');
      const customise = [...(customiser?.querySelectorAll('.btnStartCust') || [])].find(visible);
      const fields = [...(customiser?.querySelectorAll('input:not([type="hidden"]):not([type="button"]), select, textarea') || [])].filter(visible);
      return { section, info, buy, customiser, customise, fields };
    }

    modalOpen() {
      if (document.body.matches('.dyefor-customizer-open, .custom-form-active, .disable-scroll, .dyefor-mobile-menu-open, .overflow-hidden, .overflow-hidden-mobile, .overflow-hidden-tablet')) return true;
      if (document.documentElement.classList.contains('dyefor-ai-direct-product')) return true;
      if (document.querySelector('shopify-chat[open]')) return true;
      if (this.chatRoot?.querySelector('[part="activator"][aria-expanded="true"]')) return true;
      const overlays = document.querySelectorAll('cart-drawer.active, product-modal[open], dialog[open], [role="dialog"][aria-modal="true"], details-modal details[open], .dyefor-wishlist-modal:not([hidden]), .varDlgCust, .cust-form-v3');
      if ([...overlays].some(visible)) return true;
      const cookie = document.getElementById('cookieconsent:desc');
      if (cookie && visible(cookie.closest('[role="dialog"]') || cookie.parentElement)) return true;
      return document.activeElement?.matches('input:not([type="button"]):not([type="submit"]), textarea, [contenteditable="true"]') || false;
    }

    setShown(shown) {
      if (this.hidden === !shown) return;
      this.hidden = !shown;
      document.body.classList.toggle('dyefor-sticky-purchase-visible', shown);
    }

    update() {
      const chatRoot = document.querySelector('shopify-chat')?.shadowRoot;
      if (chatRoot && chatRoot !== this.chatRoot) {
        this.chatObserver?.disconnect();
        this.chatRoot = chatRoot;
        this.chatObserver = new MutationObserver(this.onUpdate);
        this.chatObserver.observe(chatRoot, { subtree: true, childList: true, attributes: true, attributeFilter: ['aria-expanded'] });
      }
      const { section, info, buy, customise, fields } = this.controls();
      if (!buy || !visible(buy)) { this.setShown(false); return; }
      const personalised = !!customise || fields.length > 0;
      const busy = this.busy || buy.classList.contains('loading');
      const disabled = busy || buy.disabled || buy.getAttribute('aria-disabled') === 'true'
        || customise?.disabled || customise?.getAttribute('aria-disabled') === 'true';
      this.button.disabled = !!disabled;
      this.button.setAttribute('aria-busy', String(!!busy));
      this.label.textContent = busy ? 'Adding...' : buy.disabled
        ? text(buy.querySelector('span')) || 'Unavailable'
        : personalised ? 'Customise' : text(buy.querySelector('span')) || window.variantStrings?.addToCart || 'Add to cart';
      this.dataset.action = personalised ? 'customise' : 'add';
      const currentPrice = [...info.querySelectorAll('.price .price-item')].filter(visible).find(element => !element.closest('s'));
      const priceText = text(currentPrice);
      if (this.price.textContent !== priceText) this.price.textContent = priceText;
      const options = [...info.querySelectorAll('variant-selects select, variant-radios input:checked')]
        .filter(element => !element.closest('noscript'))
        .map(element => element.tagName === 'SELECT' ? element.selectedOptions[0]?.textContent.trim() : element.value)
        .filter(value => value && value !== 'Default Title');
      const optionText = [...new Set(options)].join(' / ');
      if (this.variant.textContent !== optionText) this.variant.textContent = optionText;
      this.variant.title = optionText;
      const selectedImage = [...section.querySelectorAll('media-gallery .product__media img, media-gallery-colors .product__media img')].find(visible);
      const source = selectedImage?.currentSrc || selectedImage?.src;
      if (this.image && source && this.image.src !== source) {
        this.image.removeAttribute('srcset');
        this.image.src = source;
      }
      const controlsBottom = Math.max(buy.getBoundingClientRect().bottom, customise ? customise.getBoundingClientRect().bottom : -Infinity);
      this.setShown(controlsBottom < 0 && !this.modalOpen());
    }

    returnToOptions() {
      const { info, buy, customise, fields } = this.controls();
      const target = customise || fields[0] || info?.querySelector('.dyefor-device-picker__summary') || buy;
      if (!target) return;
      const top = scrollY + target.getBoundingClientRect().top - 180;
      window.scrollTo({ top: Math.max(0, top), behavior: 'instant' });
      target.focus({ preventScroll: true });
      this.schedule();
    }

    activate() {
      const { buy, customise, fields } = this.controls();
      if (this.button.disabled || !buy || buy.disabled || buy.getAttribute('aria-disabled') === 'true') return;
      if (customise) {
        this.setShown(false);
        customise.click();
        this.schedule();
        return;
      }
      if (fields.length) { this.returnToOptions(); return; }
      this.busy = true;
      this.update();
      // Forward the native click so required validation, quantity and add-ons stay intact.
      buy.click();
      this.busyTimer = setTimeout(() => {
        this.busy = false;
        const error = buy.closest('product-form')?.querySelector('.product-form__error-message-wrapper:not([hidden])');
        if (visible(error) && text(error)) this.returnToOptions();
        this.schedule();
      }, 1500);
    }
  }

  customElements.define('dyefor-sticky-purchase', StickyPurchase);
})();
